/** Timber repair bay that deliberately interrupts the galvanized perimeter. */
import * as THREE from 'three';
import { surface } from '../materials';
import type { AssetDefinition } from '../types';

const BOARD_HEIGHTS = [0.21, 0.20, 0.26, 0.22, 0.24, 0.19, 0.27, 0.23] as const;
const BOARD_GAP = 0.10 / 7;

function part(geometry: THREE.BufferGeometry, material: THREE.Material, name: string): THREE.Mesh {
  const mesh = new THREE.Mesh(geometry, material);
  mesh.name = name;
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  return mesh;
}

function boardMaterial(index: number, light: THREE.Material, dark: THREE.Material): THREE.Material {
  return index % 2 === 0 ? light : dark;
}

function addCommon(group: THREE.Group, steel: THREE.Material, timber: THREE.Material, includeCleats: boolean): void {
  // This prescribed square steel post's top box face is its flat cap. Adding a
  // separate cap would make the replacement bay falsely more elaborate than steel.
  const post = part(new THREE.BoxGeometry(0.08, 2.15, 0.08), steel, 'steel-post');
  post.position.set(0, 1.075, -1.20);
  group.add(post);
  if (!includeCleats) return;
  // Like the steel bay, this replacement owns only the -Z joint post: a second
  // one at +Z would duplicate every instanced joint and z-fight. The room's far
  // post completes the run, so the resulting asymmetric Z bound is intentional.
  for (const z of [-0.85, 0.85]) {
    const cleat = part(new THREE.BoxGeometry(0.09, 1.95, 0.05), timber, `timber-cleat-${z < 0 ? 'near' : 'far'}`);
    cleat.position.set(0, 1.075, z);
    group.add(cleat);
  }
  for (const z of [-0.85, 0.85]) {
    const nail = part(new THREE.CylinderGeometry(0.012, 0.012, 0.14, 6), steel, `cleat-nail-${z < 0 ? 'near' : 'far'}`);
    nail.rotation.z = Math.PI / 2;
    nail.position.set(0, 1.10, z);
    group.add(nail);
  }
}

function board(y: number, height: number, index: number, light: THREE.Material, dark: THREE.Material): THREE.Mesh {
  const overhang = index === 2 ? 0.04 : index === 6 ? -0.04 : 0;
  // Two reclaimed boards are 40 mm longer, with one end kept flush; merely
  // shifting a 2.40 m board would create two 20 mm offsets, not an overhang.
  const length = overhang === 0 ? 2.40 : 2.44;
  const plank = part(new THREE.BoxGeometry(0.035, height, length), boardMaterial(index, light, dark), `timber-board-${index + 1}`);
  plank.position.set(0, y, overhang / 2);
  return plank;
}

export function buildFenceBoards(state: 'whole' | 'broken'): THREE.Group {
  const group = new THREE.Group();
  // Named for the ASSET, not the state. Both states are the same object in two
  // conditions — that is the whole point of the pair — and every instrument that
  // finds an asset in a scene finds it by its registered id.
  group.name = 'fence-boards';
  group.userData['state'] = state;
  const steel = surface('metal', 0x848b90);
  const lightTimber = surface('organic', 0x6b5f4e);
  const darkTimber = surface('organic', 0x574d40);
  addCommon(group, steel, lightTimber, state === 'whole');

  if (state === 'broken') {
    /**
     * The whole repair falls in the direction Rosalie is travelling, rather than
     * leaving a tidy row of upright boards after the impact. The room turns this
     * asset by +π/2 around Y, so this local +X resting area is world -Z: the
     * downhill, field side of the fence. It is deliberately presentation-only;
     * `onBoardsGo` removes the single intact-bay collider at the same event, so
     * debris can never turn a successful escape into a new invisible obstacle.
     */
    const fallen = new THREE.Group();
    fallen.name = 'fallen-timber-panel';
    fallen.position.set(0.08, 0.025, 0);
    fallen.rotation.z = -Math.PI / 2;
    let bottom = 0.10;
    for (const [index, height] of BOARD_HEIGHTS.entries()) {
      const centreY = bottom + height / 2;
      fallen.add(board(centreY, height, index, lightTimber, darkTimber));
      bottom += height + BOARD_GAP;
    }
    // The cleats were screwed to the repair, not to the steel post. Leaving them
    // upright after the panel falls reads as a gate merely swung out of the way.
    for (const z of [-0.85, 0.85]) {
      const cleat = part(new THREE.BoxGeometry(0.09, 1.95, 0.05), lightTimber, `fallen-timber-cleat-${z < 0 ? 'near' : 'far'}`);
      cleat.position.set(0, 1.075, z);
      fallen.add(cleat);
    }
    group.add(fallen);
    return group;
  }

  let bottom = 0.10;
  for (const [index, height] of BOARD_HEIGHTS.entries()) {
    const centreY = bottom + height / 2;
    group.add(board(centreY, height, index, lightTimber, darkTimber));
    bottom += height + BOARD_GAP;
  }
  return group;
}

export const fenceBoardsAsset: AssetDefinition = {
  id: 'fence-boards',
  label: 'Timber fence repair',
  description: 'Reclaimed timber boards patched into a galvanized perimeter bay.',
  footprint: { width: 0.14, height: 2.15, length: 2.40 },
  create: () => buildFenceBoards('whole'),
};
